import { createSlice } from '@reduxjs/toolkit'
import { loadStripe } from '@stripe/stripe-js/pure'
import { actions as questionActionsF } from './questions'
import { actions as answerActionsF } from './answers'

import { deepSnakeCase } from 'Shared/functions/util'

const reducer = createSlice({
  name: 'taker',
  initialState: {
    flow: {
      actions: []
    },
    flowTaker: {},
    context: {
      template: {},
      settings: {},
      flow: {},
      client: {}
    },
    submittedResponses: {},
  },
  reducers: {
    set: (s, a) => a.payload,
    upsert: (s, a) => ({ ...s, ...a.payload })
  },
}).reducer

// if is preview then instead set up handling via postMessage...
// stub out the various server fetches

const actions = (dis, store, restClient) => {
  const questionsActions = questionActionsF(dis, store, restClient)
  const answersActions = answerActionsF(dis, store, restClient)

  let headContentInjected = false

  let stripePromise = null
  let stripeLocale = null
  function getStripePromise(locale) {
    if (store.getState().local.isPreview) return null // Don't load Stripe in embedded preview as it won't work
    if (window.ENV.CIRCLE_CI) return null // Don't load Stripe in CI - it can't function and slows down tests

    if (!stripePromise || locale !== stripeLocale) {
      stripeLocale = locale
      stripePromise = loadStripe(window.ENV.STRIPE_PUBLIC_KEY, {
        stripeAccount: window.ENV.STRIPE_ACCOUNT_ID,
        locale
      })
    }
    return stripePromise
  }

  let paypalStripePromise = null
  let paypalStripeLocale = null
  function getPaypalStripePromise(locale) {
    if (store.getState().local.isPreview) return null // Don't load Stripe in embedded preview as it won't work
    if (window.ENV.CIRCLE_CI) return null // Don't load Stripe in CI - it can't function and slows down tests

    if (!paypalStripePromise || locale !== paypalStripeLocale) {
      paypalStripeLocale = locale
      paypalStripePromise = loadStripe(window.ENV.STRIPE_ACCOUNT_PUBLIC_KEY, {
        locale
      })
    }
    return paypalStripePromise
  }

  function load(flowId, { forget, oneClickSubmit, language }) {
    const isPreview = store.getState().local.isPreview
    if (isPreview) {
      setupPreviewPostMessage()

      return Promise.resolve()
    }

    const baseParams = _.pickBy({ forget, oneClickSubmit, language }, Boolean)
    const urlParams = new URLSearchParams(window.location.search)
    const utmParams = Object.fromEntries(
      ["utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_term", "utm_content"]
        .map(key => [key, urlParams.get(key)])
        .filter(([, value]) => value)
    )
    const params = { ...baseParams, ...utmParams }

    return restClient.get(`/api/target/flows/${flowId}`, { params })
      .then(({ data }) => handleFlowData(data))
  }

  function setupPreviewPostMessage() {
    headContentInjected = false

    // iframe handling messages from parent frame
    const clientUrl = window.ENV.CLIENT_URL

    const handleMessage = async (event) => {
      if (event.origin !== clientUrl) return;

      if (event.data.type === 'FLOW_DATA') {
        handleFlowData(event.data.payload)
      }
    }
    window.addEventListener('message', handleMessage)

    postMessageToParent('FLOW_TAKER_READY')

    return () => window.removeEventListener('message', handleMessage)
  }

  function postMessageToParent(type, payload) {
    const clientUrl = window.ENV.CLIENT_URL
    window.parent.postMessage({ type, payload }, clientUrl)
  }

  function handleFlowData({ flow: camelFlow, flowTaker, targetDetails, ...rest }) {
    const templateFields = deepSnakeCase(camelFlow.templateFields)
    const flowFields = deepSnakeCase(camelFlow.flowFields)
    const flow = { ...camelFlow, templateFields, flowFields }
    const flowTemplate = rest.flowTemplate || {}

    dis({ type: 'taker/set', payload: { flow, flowTaker, targetDetails, flowTemplate } })

    // Only inject head content in preview mode on first FLOW_DATA after iframe load.
    // Subsequent updates skip injection — if HTML changes, the iframe remounts instead.
    const isPreview = store.getState().local.isPreview
    if (isPreview && !headContentInjected) {
      headContentInjected = true
      const headContent = `
        ${flow.template.head || ''}
        ${flowTemplate.additionalHead || ''}
        ${flow.additionalHead || ''}

        <style>
          ${flow.template.styles || ''}
          ${flowTemplate.additionalCss || ''}
          ${flow.additionalCss || ''}
        </style>
      `

      const startMarker = Array.from(document.head.childNodes)
        .find(node => node.nodeType === Node.COMMENT_NODE && node.textContent.includes('CUSTOM_HEAD_START'))
      const endMarker = Array.from(document.head.childNodes)
        .find(node => node.nodeType === Node.COMMENT_NODE && node.textContent.includes('CUSTOM_HEAD_END'))

      // Remove everything between start and end markers
      while (startMarker.nextSibling && startMarker.nextSibling !== endMarker) {
        startMarker.nextSibling.remove()
      }

      // Insert new content before the end marker
      const template = document.createElement('template')
      template.innerHTML = headContent
      const content = template.content

      // Remove Stripe scripts - Stripe doesn't work in preview and creates empty iframes
      content.querySelectorAll('script[src*="stripe"]').forEach(el => el.remove())

      // Scripts inserted via innerHTML don't execute, so we need to re-create them
      content.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script')
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value)
        })
        newScript.textContent = oldScript.textContent
        oldScript.parentNode.replaceChild(newScript, oldScript)
      })

      // Manually add a meta tag to indicate the preview is loaded so tests can wait for this instead of relying on arbitrary timeouts
      const metaPreviewLoaded = document.createElement('meta')
      metaPreviewLoaded.name = 'preview-loaded'
      metaPreviewLoaded.content = 'true'
      endMarker.parentNode.insertBefore(metaPreviewLoaded, endMarker)

      endMarker.parentNode.insertBefore(content, endMarker)
    }

    const surveys = rest.surveys || _.compact(flow.actions.map(a => a.survey))
    const questions = rest.questions || _.flatMap(surveys, s => s.questions)
    const answers = rest.answers || _.flatMap(questions, q => q.answers)

    questionsActions.setAll(questions)
    answersActions.setAll(answers)

    if (rest.language) {
      dis({ type: 'local/upsert', payload: { language: rest.language } })
    }
  }

  function submitPage({ pageNumber, actionId, responses, language = null }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id
    language ||= store.getState().local.language

    return restClient.post(`/api/target/flows/${flowId}/answer_survey`, { pageNumber, actionId, responses, flowTakerId, language })
      .then(({ data }) => {
        const { flowTaker, responses } = data
        dis({ type: 'taker/upsert', payload: { flowTaker: flowTaker, submittedResponses: responses } })
      })
  }

  function reportShare({ actionId, shareChannel }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/flows/${flowId}/report_share`, { actionId, shareChannel, flowTakerId })
  }

  function updateNoTarget() {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/flows/${flowId}/update_no_target`, { flowTakerId })
      .then(({ data }) => {
        const { flowTaker } = data
        dis({ type: 'taker/upsert', payload: { flowTaker, targetDetails: { signedIn: false } } })
      })
  }

  function completeAction({ actionId, actionParams = {}, language = null }) {
    const state = store.getState()
    const flowTakerId = state.taker.flowTaker.id
    const flow = state.taker.flow
    language ||= store.getState().local.language

    const flowId = flow.id
    const params = { actionId, flowTakerId, language }
    const action = flow.actions.find(a => a.id === actionId)
    if (action.kind === 'donate') {
      if (actionParams.stripeSessionId) {
        params.stripeSessionId = actionParams.stripeSessionId
      } else if (actionParams.stripePaymentIntentId) {
        params.stripePaymentIntentId = actionParams.stripePaymentIntentId
      }
      params.useMainAccount = actionParams.useMainAccount === true
      params.stripeSubscriptionId = actionParams.stripeSubscriptionId || null
    } else if (action.kind === 'email_to_target') {
      params.subject = actionParams.subject
      params.body = actionParams.body
      params.decisionMakerIds = actionParams.decisionMakerIds
      params.name = actionParams.name
      params.email = actionParams.email
      params.postcode = actionParams.postcode
      params.address_1 = actionParams.address_1
      params.city = actionParams.city
      params.testMode = state.local.testMode
      params.optIn = actionParams.optIn
    }

    return restClient.post(`/api/target/flows/${flowId}/complete_action`, params)
      .then(({ data }) => {
        const { flowTaker } = data
        dis({ type: 'taker/upsert', payload: { flowTaker } })
      })
  }

  function skipAction({ actionId }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/flows/${flowId}/skip_action`, { actionId, flowTakerId })
      .then(({ data }) => {
        const { flowTaker } = data
        dis({ type: 'taker/upsert', payload: { flowTaker } })
      })
  }

  function createStripeCheckoutSession({ amountInMinorUnits, isMonthly, coverFee, currency, locale }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/stripe/checkout_session`, { amountInMinorUnits, isMonthly, coverFee, flowTakerId, flowId, currency, locale })
  }

  function createStripePaymentIntent({ amountInMinorUnits, isMonthly, coverFee, billingDetails, currency, useMainAccount = false }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/stripe/payment_intent`, {
      amountInMinorUnits, isMonthly, coverFee, flowTakerId, flowId, useMainAccount, billingDetails, currency
    })
  }

  function setResponse({ questionId, answerId, answerIds, answerText }) {
    const state = store.getState()
    const responses = _.filter(state.taker?.submittedResponses || {}, (v, k) => v?.questionId !== questionId)
    responses.push({ questionId, answerId, answerIds, answerText })

    dis({ type: 'taker/upsert', payload: { submittedResponses: responses } })
  }

  return {
    load,
    submitPage,
    reportShare,
    updateNoTarget,
    completeAction,
    skipAction,
    set: (payload) => {
      dis({ type: 'taker/set', payload })
    },
    createStripeCheckoutSession,
    createStripePaymentIntent,
    setResponse,
    setupPreviewPostMessage,
    postMessageToParent,
    getStripePromise,
    getPaypalStripePromise
  }
}

export default { reducer, actions }
